import { z } from "zod";
import {
  assistantContextSchema,
  assistantJsonSchema,
  assistantPolicySchema,
} from "./assistant-protocol";
// Bound schema growth by wire bytes, not the number of registered capabilities.
// The whole request shares the existing 2 MB transport envelope; any individual
// catalog entry receives at most 1/32 of it so one recursive schema cannot
// monopolize model context. No registered capabilities are silently removed.
export const ASSISTANT_REQUEST_MAX_BYTES = 2000000;
export const ASSISTANT_TOOL_MAX_BYTES = 65536;
export function assistantJsonByteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}
const identifier = z.string().min(1).max(500);
export const assistantModelCallSchema = z
  .object({
    callId: identifier,
    toolName: identifier,
    arguments: z.record(assistantJsonSchema),
  })
  .strict();
export const assistantModelTurnSchema = z
  .object({
    content: z.string().max(100000),
    toolCalls: z.array(assistantModelCallSchema).max(8),
  })
  .strict();
export const assistantConversationSchema = z
  .array(
    z.discriminatedUnion("role", [
      z
        .object({
          role: z.literal("user"),
          content: z.string().min(1).max(30000),
        })
        .strict(),
      z
        .object({
          role: z.literal("assistant"),
          content: z.string().max(100000),
          toolCalls: z.array(assistantModelCallSchema).max(8),
        })
        .strict(),
      z
        .object({
          role: z.literal("tool"),
          toolCallId: identifier,
          content: z.string().max(1000000),
        })
        .strict(),
    ]),
  )
  .max(200);
export type AssistantModelTurn = z.infer<typeof assistantModelTurnSchema>;
export type AssistantConversation = z.infer<typeof assistantConversationSchema>;

export const assistantModelRequestSchema = z
  .object({
    context: assistantContextSchema,
    messages: assistantConversationSchema,
    tools: z.array(
      z
        .object({
          toolName: identifier,
          title: z.string().max(500),
          description: z.string().max(10000),
          inputSchema: assistantJsonSchema,
          outputSchema: assistantJsonSchema,
          policy: assistantPolicySchema,
        })
        .strict()
        .refine(
          (tool) => assistantJsonByteLength(tool) <= ASSISTANT_TOOL_MAX_BYTES,
          "Assistant tool schema exceeds its transport budget",
        ),
    ),
    /** Opt-in: absent means false; only opted-in callers receive `usage`. */
    includeUsage: z.boolean().optional(),
  })
  .strict()
  .refine(
    (request) =>
      assistantJsonByteLength(request) <= ASSISTANT_REQUEST_MAX_BYTES,
    "Assistant request exceeds its context budget",
  );
export type AssistantModelRequest = z.infer<typeof assistantModelRequestSchema>;

/** Upper bound for one normalized token count; larger values are not credible. */
export const ASSISTANT_USAGE_MAX_TOKENS = 100000000;
const usageCount = z.number().int().min(0).max(ASSISTANT_USAGE_MAX_TOKENS);
export const assistantModelUsageSchema = z
  .object({ inputTokens: usageCount, outputTokens: usageCount })
  .strict();
export type AssistantModelUsage = z.infer<typeof assistantModelUsageSchema>;

export const assistantModelResponseSchema = assistantModelTurnSchema.extend({
  /** Configured requested model. It does not prove which model served the call. */
  model: z.string().min(1).max(500),
  /** Actual provider-returned model identity, when explicitly supplied. */
  providerModel: z.string().min(1).max(500).optional(),
  /** Provider-reported token counts, when present and well formed. */
  usage: assistantModelUsageSchema.optional(),
});
export type AssistantModelResponse = z.infer<
  typeof assistantModelResponseSchema
>;

const modelFailureMessages = {
  rateLimited:
    "The AI provider is rate-limiting requests (429). Try again later.",
  outputLimit:
    "The AI provider reached its response limit. No operations from the truncated response were dispatched; check any earlier changes before trying again.",
  capacity: "The assistant is at capacity. Try again later.",
  timeout:
    "The AI provider timed out. Check any earlier changes before trying again.",
  unavailable: "The AI provider is unavailable. Check the model connection.",
  invalid:
    "The AI provider returned an invalid response. Check any earlier changes before trying again.",
  configuration: "The AI provider is not configured.",
};

/** Only fixed messages cross the iframe boundary; never forward provider text. */
export function getAssistantModelFailureMessage(
  error: unknown,
): string | undefined {
  // Comlink preserves Error.message, but need not preserve subclass/custom fields.
  if (error instanceof Error) {
    return Object.values(modelFailureMessages).find(
      (message) => message === error.message,
    );
  }
  const parsed = z
    .object({
      error: z.object({
        code: z.enum([
          "PROVIDER_HTTP_ERROR",
          "PROVIDER_TIMEOUT",
          "PROVIDER_TRANSPORT_ERROR",
          "PROVIDER_BODY_ERROR",
          "PROVIDER_COMPLETION_ERROR",
          "PROVIDER_OUTPUT_LIMIT",
          "PROVIDER_TOOL_CALL_ERROR",
          "ASSISTANT_CAPACITY_BUSY",
          "PROVIDER_NOT_CONFIGURED",
        ]),
        providerStatus: z.number().int().min(100).max(599).optional(),
      }),
    })
    .safeParse(error);
  if (!parsed.success) return undefined;
  const { code, providerStatus } = parsed.data.error;
  if (code === "PROVIDER_HTTP_ERROR" && providerStatus === 429)
    return modelFailureMessages.rateLimited;
  if (code === "ASSISTANT_CAPACITY_BUSY") return modelFailureMessages.capacity;
  if (code === "PROVIDER_NOT_CONFIGURED")
    return modelFailureMessages.configuration;
  if (code === "PROVIDER_TIMEOUT") return modelFailureMessages.timeout;
  if (code === "PROVIDER_OUTPUT_LIMIT") return modelFailureMessages.outputLimit;
  if (code === "PROVIDER_HTTP_ERROR" || code === "PROVIDER_TRANSPORT_ERROR")
    return modelFailureMessages.unavailable;
  return modelFailureMessages.invalid;
}
